import { Observable } from 'rxjs';
import { $httpClient } from '@/services/httpClient';
import {
  CREATE_FORMS_FILTERS,
  CREATE_STATISTICS,
  EMPLOYEE_DETAILS_UPDATE,
  EMPLOYEE_REMINDERS_PRE_SEND,
  EMPLOYEE_REMINDERS_SEND,
  GET_JOB_LOG,
  GET_JOBS_LOGS_LIST,
  GET_QUARTZ_INFO,
  GET_SYSTEM_CACHE_LIST,
  MAINTENANCE_PAGE,
  PAUSE_QUARTZ_TRIGGER,
  PROCESS_COMPARE_REQUESTS,
  REFRESH_APPLICATION_USER_VIEW,
  RELOAD_CACHE,
  RESUME_QUARTZ_TRIGGER,
  RUN_FORM_101_PDF_COMSIGN_JOB,
  RUN_FORM_101_PDF_FILES_EXPORT_JOB,
  RUN_FORM_101_PDF_GENERATOR_JOB,
  STANDBY_QUARTZ_SCHEDULER, START_QUARTZ_SCHEDULER,
  TEST_MAILS,
  UPDATE_FORM_101_FILTERS,
  REFRESH_INDEX_DATA_BASE,
  STATISTICS_REPORT,
} from '@/types/system.types';
import { SystemLogin } from '@/models/system/SystemLogin';
import { RefreshCache } from '@/models/system/RefreshCache';
import { QuartzInfo } from '@/models/system/QuartzInfo';
import { FormsFiltersModel } from '@/models/system/FormsFilters';
import { RunForm101PdfJobRequest } from '@/models/system/RunForm101PdfJobRequest';
import { TestMailsRequest } from '@/models/system/TestMailsRequest';
import { CustomerDetailsModel } from '@/models/backoffice/CustomerDetails';
import { BaseResponse } from '@/models/response/base/BaseResponse';
import { StatisticsReportRequest } from '@/models/system/StatisticsReportRequest';

export default class SystemService {
  private readonly baseApi: string;

  constructor() {
    this.baseApi = '/system-info/';
  }

  public checkCredentials(credentials: SystemLogin): Observable<{ body: string }> {
    return $httpClient.post<{ body: string }>('system/systemInfoLogin', credentials);
  }

  public systemInfoAccessible(): Observable<any> {
    return $httpClient.get<any>(this.baseApi + 'systemInfoAccessible');
  }

  public getSystemCacheList(): Observable<any> {
    return $httpClient.get(this.baseApi + GET_SYSTEM_CACHE_LIST);
  }

  public reloadCache(refreshCache: RefreshCache): Observable<any> {
    return $httpClient.post<any>(this.baseApi + RELOAD_CACHE + '?cacheName=' + refreshCache.cacheName);
    // const $httpRefresh = new HttpClient(HttpClient.getGlobalConfig(refreshCache.hostName));
    // $httpRefresh.addRequestInterceptor(systemInfoTokenInterceptor);
    //
    // return from(
    //   $httpRefresh.post(
    //   `https://${refreshCache.hostName}/form-101${this.baseApi}${RELOAD_CACHE}?cacheName=${refreshCache.cacheName}`,
    //   ),
    // );
  }

  public getJobsLogsList(searchJobsObject: any): Observable<any> {
    return $httpClient.post(this.baseApi + GET_JOBS_LOGS_LIST, searchJobsObject);
  }

  public getJobLog(jobId: number): Observable<any> {
    return $httpClient.get(`${this.baseApi}${GET_JOB_LOG}?jobId=${jobId}`);
  }

  public getQuartzInfo(): Observable<QuartzInfo> {
    return $httpClient.get(this.baseApi + GET_QUARTZ_INFO);
  }


  public pauseQuartzTrigger(triggerName: string): Observable<any> {
    return $httpClient.post(`${this.baseApi}${PAUSE_QUARTZ_TRIGGER}?triggerName=${triggerName}`);
  }

  public resumeQuartzTrigger(triggerName: string): Observable<any> {
    return $httpClient.post(`${this.baseApi}${RESUME_QUARTZ_TRIGGER}?triggerName=${triggerName}`);
  }

  public startQuartzScheduler(): Observable<any> {
    return $httpClient.post(this.baseApi + START_QUARTZ_SCHEDULER);
  }

  public standbyQuartzScheduler(): Observable<any> {
    return $httpClient.post(this.baseApi + STANDBY_QUARTZ_SCHEDULER);
  }

  public createStatistics(): Observable<any> {
    return $httpClient.post(this.baseApi + CREATE_STATISTICS);
  }

  public createFormsFilters(formsFilters: FormsFiltersModel): Observable<any> {
    return $httpClient.get(this.baseApi + CREATE_FORMS_FILTERS +
      '?taxYear=' + formsFilters.taxYear +
      (formsFilters.officeNum ? '&officeNums=' + formsFilters.officeNum : '') +
      (formsFilters.firstResult ? '&firstResult=' + formsFilters.firstResult : '') +
      (formsFilters.maxResults ? '&maxResults=' + formsFilters.maxResults : '') +
      (formsFilters.asJob ? '&asJob=' + formsFilters.asJob : '') +
      (formsFilters.detailedLog ? '&detailedLog=' + formsFilters.detailedLog : ''),
    );
  }

  public runForm101PdfGeneratorJob(runForm101PdfGeneratorJobObj: RunForm101PdfJobRequest): Observable<any> {
    return $httpClient.get(this.baseApi + RUN_FORM_101_PDF_GENERATOR_JOB +
      '?taxYear=' + runForm101PdfGeneratorJobObj.taxYear +
      (runForm101PdfGeneratorJobObj.indexNums ? '&indexNums=' + runForm101PdfGeneratorJobObj.indexNums : ''),
    );
  }

  public runForm101PdfFilesExportJob(runForm101PdfFilesExportJob: RunForm101PdfJobRequest): Observable<any> {
    return $httpClient.get(this.baseApi + RUN_FORM_101_PDF_FILES_EXPORT_JOB +
      '?taxYear=' + runForm101PdfFilesExportJob.taxYear +
      (runForm101PdfFilesExportJob.indexNum ? '&indexNum=' + runForm101PdfFilesExportJob.indexNum : '') +
      (runForm101PdfFilesExportJob.indexNums ? '&officeNums=' + runForm101PdfFilesExportJob.officeNums : ''),
    );
  }

  public runForm101PdfComsignJob(runForm101PdfComsignJob: RunForm101PdfJobRequest): Observable<any> {
    return $httpClient.get(this.baseApi + RUN_FORM_101_PDF_COMSIGN_JOB +
      '?taxYear=' + runForm101PdfComsignJob.taxYear +
      (runForm101PdfComsignJob.indexNum ? '&indexNum=' + runForm101PdfComsignJob.indexNum : '') +
      (runForm101PdfComsignJob.officeNums ? '&officeNums=' + runForm101PdfComsignJob.indexNum : ''),
    );
  }

  public employeeRemindersSend(): Observable<any> {
    return $httpClient.post(this.baseApi + EMPLOYEE_REMINDERS_SEND);
  }

  public employeeRemindersPreSend(): Observable<any> {
    return $httpClient.post(this.baseApi + EMPLOYEE_REMINDERS_PRE_SEND);
  }

  public testMails(testMailsRequest: TestMailsRequest): Observable<any> {
    return $httpClient.post(this.baseApi + TEST_MAILS, testMailsRequest);
  }

  public statisticsReport(statisticsReportRequest: StatisticsReportRequest): Observable<BaseResponse> {
    return $httpClient.post(this.baseApi + STATISTICS_REPORT
      + `?date2fetch=${statisticsReportRequest.date2fetch}&email=${statisticsReportRequest.email}`);
  }

  public processCompareRequests(
    taxYear: number, indexNums: string, maxResult: number, firstResult: number,
  ): Observable<CustomerDetailsModel> {
    return $httpClient.get<CustomerDetailsModel>(this.baseApi + PROCESS_COMPARE_REQUESTS +
      '?taxYear=' + taxYear +
      (indexNums ? '&indexNums=' + indexNums : '') +
      (firstResult ? '&firstResult=' + firstResult : '') +
      (maxResult ? '&maxResults=' + maxResult : ''),
    );
  }

  public employeeDetailsUpdate(): Observable<CustomerDetailsModel> {
    return $httpClient.get<CustomerDetailsModel>(this.baseApi + EMPLOYEE_DETAILS_UPDATE);
  }

  public refreshApplicationUserView(): Observable<BaseResponse> {
    return $httpClient.get<BaseResponse>(this.baseApi + REFRESH_APPLICATION_USER_VIEW);
  }

  public maintenancePage(turnOnOrOff: boolean): Observable<any> {
    return $httpClient.get(this.baseApi + MAINTENANCE_PAGE + '?turnOn=' + turnOnOrOff);
  }

  public updateForm101Filters(): Observable<BaseResponse> {
    return $httpClient.get(this.baseApi + UPDATE_FORM_101_FILTERS);
  }

  public refreshIndexDatabase(): Observable<BaseResponse> {
    return $httpClient.post(this.baseApi + REFRESH_INDEX_DATA_BASE);
  }

}
